import NotificationCard from "./NotificationCard";
import { differenceInSeconds, formatDistanceToNow } from "date-fns";

import { getItem } from "../../utils/storage";

const getToken = () => {
  const tokens = ["adminToken", "workerToken", "customerToken", "donorToken"];
  for (const token of tokens) {
    const value = getItem(token);
    if (value) return token;
  }
  return null;
};

const checkNotificationIsUnread = (notification) => {
  const user = getToken();
  let isUnread;
  if (user === "workerToken") {
    return !notification?.IsViewByWorker;
  } else if (user === "customerToken") {
    isUnread = !notification?.IsViewByCustomer;
  } else if (user === "donorToken") {
    isUnread = !notification?.IsViewByDonor;
  } else if (user === "adminToken") {
    isUnread = !notification?.IsView;
  }

  return isUnread;
};

const formatTimeAgo = (AddedAt, currentAt) => {
  if (!AddedAt) return "";

  const timeDiffInSeconds = differenceInSeconds(currentAt, AddedAt);
  let formattedTime = "";

  if (timeDiffInSeconds < 60) {
    formattedTime = `${timeDiffInSeconds} second${
      timeDiffInSeconds !== 1 ? "s" : ""
    } ago`;
  } else if (timeDiffInSeconds < 3600) {
    const timeDiffInMinutes = Math.floor(timeDiffInSeconds / 60);
    formattedTime = `${timeDiffInMinutes} minute${
      timeDiffInMinutes !== 1 ? "s" : ""
    } ago`;
  } else {
    formattedTime = formatDistanceToNow(AddedAt, { addSuffix: true });
  }

  return formattedTime;
};

const formatStyle = (isUnread) => {
  const notificationStyle = {
    backgroundColor: "#ffffff",
    padding: "12px 16px",
    borderRadius: "12px",
    marginBottom: "12px",
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    boxShadow: "var(--shadow-sm)",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
  };
  if (!isUnread) {
    notificationStyle.transform = "scale(1.02)";
    notificationStyle.boxShadow =
      "var(--shadow-lg), var(--shadow-md)";
    notificationStyle.borderLeft = "4px solid #D4943A";
    notificationStyle.backgroundColor = "#f8fafc";
    notificationStyle.borderRadius = "12px";
    notificationStyle.padding = "12px 16px";
    notificationStyle.marginBottom = "16px";
  }
  return notificationStyle;
};

const getNavigationIdAndType = (notification) => {
	const isDonation = notification?.Is_Donor === true;
	let navigationId, typeOfPage, documentType;
	if (notification?.NotificationForType === "Invoice") {
		navigationId = notification?.InvoiceId;
		typeOfPage = "invoice";
		documentType = "Invoice";
	} else if (notification?.NotificationForType === "SignatureRequest") {
		navigationId = notification?.signatureRequestId;
		typeOfPage = "signature";
		documentType = "Contract";
	} else if (notification.NotificationForType === "ACHPayment") {
		if (notification?.CreatedFrom === "Billing-History") {
			navigationId = notification?.DonorId || notification?.CustomerId;
			typeOfPage = "billing-history";
			documentType = isDonation ? "Donation" : "ACH Payment";
		} else if (notification?.CreatedFrom === "Invoice-Payment") {
			navigationId = notification?.InvoiceId;
			typeOfPage = "invoice";
			documentType = "Invoice";
		}
	} else if (notification?.NotificationForType === "Customer") {
		navigationId = notification?.CustomerId;
		typeOfPage = "customer";
	} else if (notification?.NotificationForType === "Donor") {
		navigationId = notification?.DonorId || notification?.WorkerId;
		typeOfPage = notification?.DonorId ? "donor" : "worker";
	} else if (notification.NotificationForType === "Worker") {
		navigationId = notification?.WorkerId;
		typeOfPage = "worker";
	} else if (notification?.NotificationForType === "CardPayment") {
		if (notification?.CreatedFrom === "Billing-History"||notification?.CreatedFrom === "Donation-Page") {
			navigationId = notification?.DonorId || notification?.CustomerId;
			typeOfPage = "billing-history";
		} else if (notification?.CreatedFrom === "Invoice-Payment") {
			navigationId = notification?.InvoiceId;
			typeOfPage = "invoice";
		}
	} else if (notification?.NotificationForType === "Payment") {
		if (notification?.CreatedFrom === "Billing-History") {
			navigationId = notification?.DonorId || notification?.CustomerId;
			typeOfPage = "billing-history";
			documentType = isDonation ? "Donation" : "Payment";
		} else if (notification?.CreatedFrom === "Invoice-Payment") {
			navigationId = notification?.InvoiceId;
			typeOfPage = "invoice";
		}
	} else if (notification.NotificationForType === "Quote") {
		navigationId = notification?.QuoteId;
		typeOfPage = "quote";
	} else if (notification.NotificationForType === "Quote") {
		navigationId = notification?.QuoteId;
		typeOfPage = "quote";
	} else if (notification.NotificationForType === "Contract") {
		navigationId = isDonation
			? notification?.DonorId || notification?.CustomerId
			: notification?.ContractId;
		typeOfPage = "contract";
	} else if (notification.NotificationForType === "Visit") {
		navigationId = notification?.ContractId;
		typeOfPage = "contract";
	} else if (notification?.NotificationForType === "RecurringCharge") {
		navigationId = notification?.DonorId || notification?.CustomerId;
		typeOfPage = "recurring-charges";
	} else if (notification?.NotificationForType === "Charge") {
		navigationId = notification?.DonorId || notification?.CustomerId;
		typeOfPage = "billing-history";
	} else if (notification?.NotificationForType === "RecurringPayment"||notification?.NotificationForType === "Recurring Payment") {
		navigationId = notification?.CustomerId;
		typeOfPage = "recurring-payment";
	} else if (notification.NotificationForType === "Material") {
		navigationId = notification?.ProductId;
		typeOfPage = "productandservice";
	} else if (notification.NotificationForType === "Account") {
		navigationId = notification?.account_id;
		typeOfPage = "account";
	} else if (notification.NotificationForType === "Template") {
		navigationId = notification?.TemplateId;
		typeOfPage = "templates";
	} else if (notification?.NotificationForType === "Company") {
		// For SuperAdmin, navigate to company management
		navigationId = notification?.CompanyId;
		typeOfPage = "company"; // This will need to be handled in NotificationPage
	}
	return { navigationId, typeOfPage };
};

const NotificationContainer = ({ handleNotificationClick, notification }) => {
  const AddedAt = notification?.AddedAt;
  const isUnread = checkNotificationIsUnread(notification);
  const notificationStyle = formatStyle(isUnread);

  const formatName = (firstName, lastName) => {
    if (!firstName && !lastName) return "";
    return `${firstName || ""} ${lastName || ""}`.trim();
  };

  const userType = getToken();
  const isDonation = notification?.Is_Donor === true;

  let { navigationId, typeOfPage } = getNavigationIdAndType(notification);
  let notificationId = notification?.NotificationId;

  let message;

  if (userType === "adminToken") {
    message = notification?.MessageForAdmin;
  } else if (userType === "customerToken") {
    message = notification?.MessageForCustomerOrDonor;
  } else if (userType === "donorToken") {
    message = notification?.MessageForCustomerOrDonor;
  } else if (userType === "workerToken") {
    message = notification?.MessageForWorker;
  }

  if (notification?.CreatedByUser?.FirstName == undefined) {

    message = message?.replace("{CreatedByUserName}", notification?.OwnerName);
  } else {
    const createdByName = formatName(
      notification?.CreatedByUser?.FirstName,
      notification?.CreatedByUser?.LastName
    );
    message = message?.replace("{CreatedByUserName}", createdByName);
  }
  const customerOrDonorName = formatName(
    notification?.FirstName || "Walk",
    notification?.LastName || "In"
  );
  message = message?.replace("{CustomerOrDonorName}", customerOrDonorName);
  message = message?.replace("{OwnerName}", notification?.OwnerName);
  
  // For Company notifications, replace with company name
  if (notification?.NotificationForType === "Company" && notification?.CompanyName) {
    message = message?.replace("{CompanyName}", notification?.CompanyName);
  }
  
  // Replace "customer message" with "customer event" for CustomerMessage notifications
  if (notification?.NotificationForType === "CustomerMessage") {
    message = message?.replace(/customer message/gi, "customer event");
    message = message?.replace(/Customer Message/gi, "Customer Event");
  }

  let notificationData = {
    isDonation: isDonation,
    message: message,
    createdBy: notification?.CreatedBy,
    firstName: notification?.FirstName,
    lastName: notification?.LastName,

    ownerName: notification?.OwnerName,
    createdByUserFullName: notification?.CreatedByUser?.FirstName
      ? formatName(
          notification?.CreatedByUser?.FirstName,
          notification?.CreatedByUser?.LastName
        )
      : notification?.OwnerName,

    createdByUserRole: notification?.CreatedByUserRole,
    title: notification?.Title,
    documentType: notification?.NotificationForType,
    documentNumber: notification?.DocumentNumber,
    total: notification?.Total,

    status: notification?.Status,

    startDate: notification?.StartDate,

    name: notification?.Name,
    type: notification?.Type,

    requestMessage: notification?.RequestMessage,
    dropBoxRequesterEmailAddress: notification?.DropBoxRequesterEmailAddress,
    cardType: notification?.CardType,
    amount: notification?.Amount,

    paymentAmount: notification?.Activity?.amount,
    paymentDescription: notification?.Activity?.description,
    paymentMethod: notification?.Activity?.method,
    paymentStatus: notification?.Activity?.status,
    paymentReason: notification?.Activity?.reason,
    paymentTransactionId: notification?.Activity?.transactionId,

    formattedTime: AddedAt ? formatTimeAgo(AddedAt, new Date()) : "",
    
    // Company information for SuperAdmin notifications
    companyName: notification?.CompanyName,
    companyEmail: notification?.CompanyEmail,
  };

  return (
    <NotificationCard
      handleNotificationClick={() =>
        handleNotificationClick(navigationId, notificationId, typeOfPage)
      }
      style={notificationStyle}
      isUnread={isUnread}
      notificationData={notificationData}
    />
  );
};

export default NotificationContainer;
